import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Loader2, Search, UsersRound, ExternalLink, ArrowLeft } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { bizDate } from '@/lib/dates';
import { toast } from 'sonner';

const ACCESS_TONE = {
  not_invited: 'bg-muted text-muted-foreground border-border',
  invited: 'bg-info/10 text-info border-info/20',
  active: 'bg-success/10 text-success border-success/20',
  blocked: 'bg-destructive/10 text-destructive border-destructive/20',
};

/** Founder UAT R3 — client-facing interface over EXISTING external-candidate
 * records (aggregated by email, tenant-scoped). NOT a second database. */
export default function ClientExternalCandidates() {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const load = useCallback(async (needle) => {
    setLoading(true);
    try {
      const r = await api.get('/client/external-candidates', { params: needle ? { q: needle } : {} });
      setItems(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); setItems([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const accessLabel = (s) => ({
    not_invited: L('Pendiente de invitación', 'Invitation pending'),
    invited: L('Invitación enviada', 'Invitation issued'),
    active: L('Acceso activo', 'Access active'),
    blocked: L('Bloqueado', 'Blocked'),
  }[s] || s);

  return (
    <div className="space-y-6" data-testid="external-candidates-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <UsersRound className="h-6 w-6 text-primary" />
            {L('Candidatos externos', 'External Candidates')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {L('Población de candidatos externos de tu organización. Un candidato externo no es un colaborador interno: participa únicamente en procesos de selección.',
               'Your organization’s external candidate population. An external candidate is not an internal collaborator: they participate only in recruitment processes.')}
          </p>
        </div>
        <Link to="/client/talentcheck" className="ml-auto">
          <Button size="sm" variant="outline" className="gap-1.5" data-testid="extcand-back">
            <ArrowLeft className="h-4 w-4" /> TalentCheck
          </Button>
        </Link>
      </div>

      <Card className="p-5 shadow-card">
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input className="pl-8" placeholder={L('Buscar por nombre o correo…', 'Search by name or email…')}
                   value={q}
                   onChange={(e) => { const v = e.target.value; setQ(v); load(v); }}
                   data-testid="extcand-search" />
          </div>
          <Button size="sm" variant="secondary" onClick={() => load(q)} data-testid="extcand-search-btn">
            {L('Buscar', 'Search')}
          </Button>
          <Badge variant="outline" className="ml-auto tabular-nums">{items.length}</Badge>
        </div>

        {loading ? (
          <div className="py-10 flex justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <p className="py-8 text-sm text-muted-foreground text-center" data-testid="extcand-empty">
            {L('Aún no hay candidatos externos registrados. Los candidatos que agregues a tus procesos aparecerán aquí automáticamente.',
               'No external candidates yet. Candidates you add to your processes will appear here automatically.')}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{L('Candidato', 'Candidate')}</TableHead>
                <TableHead className="hidden md:table-cell">{L('Contacto', 'Contact')}</TableHead>
                <TableHead>{L('Procesos', 'Processes')}</TableHead>
                <TableHead>{L('Evaluaciones', 'Assessments')}</TableHead>
                <TableHead>{L('Acceso', 'Access')}</TableHead>
                <TableHead className="hidden lg:table-cell">{L('Última actividad', 'Last activity')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.email} data-testid={`extcand-row-${c.email}`}>
                  <TableCell>
                    <p className="font-medium">{c.name || '—'}</p>
                    <p className="text-xs text-muted-foreground">{c.email}</p>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                    <p>{c.phone || '—'}</p>
                    {c.cv_url && (
                      <a href={c.cv_url} target="_blank" rel="noreferrer"
                         className="text-primary inline-flex items-center gap-1 hover:underline">
                        CV <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      {(c.processes || []).slice(0, 3).map((p) => (
                        <Link key={`${c.email}-${p.id}`} to={`/client/recruitment/${p.id}`}
                              className="text-xs text-primary hover:underline">
                          {p.position_title}{p.closed ? L(' (cerrado)', ' (closed)') : ''}
                        </Link>
                      ))}
                      {(c.processes || []).length > 3 && (
                        <span className="text-xs text-muted-foreground">+{c.processes.length - 3}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs tabular-nums">
                    {c.assessments_completed}/{c.assessments_assigned} {L('completadas', 'completed')}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs ${ACCESS_TONE[c.access_status] || ''}`}>
                      {accessLabel(c.access_status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">
                    {bizDate(c.last_activity, i18n.language)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
